import {nanoid} from 'nanoid';
import {useCallback, useEffect, useRef} from 'react';

import {TasksObserver} from 'services/TasksObserver/TasksObserver';
import {useTasksObserver} from 'services/TasksObserver/TasksObserverProvider';

export function useTasksSubscription(
  callback: Parameters<InstanceType<typeof TasksObserver>['subscribe']>[0],
  config: Parameters<InstanceType<typeof TasksObserver>['subscribe']>[1],
  id?: string,
) {
  const observer = useTasksObserver();
  const subscriptionId = useRef(id || nanoid());

  const stableCallback = useCallback(callback, [callback]);

  // Use ref for callback to avoid effect retriggering
  const callbackRef = useRef(stableCallback);
  callbackRef.current = stableCallback;

  useEffect(() => {
    const unsubscribe = observer.subscribe(
      (...args) => {
        // Wrap callback execution in requestAnimationFrame to batch updates
        requestAnimationFrame(() => {
          callbackRef.current(...args);
        });
      },
      config,
      subscriptionId.current,
    );

    return () => {
      unsubscribe();
    };
  }, [observer, config]);

  return [subscriptionId.current, observer] as const;
}
